<template>
  <div>
    <popup-hint hint="tanks">
      <template v-slot:breadcrumbs>
        <breadcrumbs home="Tanks">
          <template v-slot:left v-if="maintenanceDueCount">
            <list-notification v-model="showStatus">
              <v-list-item>
                <v-icon>mdi-arrow-right-thin</v-icon>
                  {{maintenanceDueCount}} tank{{maintenanceDueCount>1?'s':''}} due for maintenance
                <v-spacer/>
                <v-btn small color="warning" @click="showStatus=false;$nextTick(() => {filters.siteId=null;filters.status='1';filters.license='2';filters.contents='0';filters.maintenanceDue='1';filters.asof=today})">View</v-btn>
              </v-list-item>
            </list-notification>
          </template>
        </breadcrumbs>
      </template>
      <template v-slot:top>
        Tanks are required for production, as they hold spirits (or wine)
        used in distilling, blending and bottling.
      </template>
      <template v-slot:bottom>
        You can create tanks individually or do a batch import of tanks.
      </template>
      <template v-slot:actions="{close}">
        <v-btn color="primary" class="ml-4" small @click="close();$refs.import.doImport()">Import</v-btn>
        <v-btn @click="close()" :to="{name:'EquipmentTanksEditNew'}" color="primary" class="ml-4" small>Create</v-btn>
      </template>
    </popup-hint>
    <import-csv v-model="doImport" type="tanks" :fields="importFields" importAction="tanksImport" :instructions="instructions" ref="import" @refresh="refresh"/>

    <purtrak-table name="tanks" objectType="tanks" :defaultColumns="defaultColumns" :headers="headers" ref="table" show-select show-expand
                   listEndPoint="tanks2" v-model="selected" @list="listBuilt"
                   fields="id,name,site{id,name},active,capacity,
                                                  manufacturer,modelNumber,serialNumber,maintenanceDue,
                                                  contents{volume,litres,value,strength,kg,alcoholName,alcoholType,proofed,lot,userLicense,imported}"
                   totalFields="totalVolume,totalValue,totalLitres"
                   :totals.sync="totals"
                   :listFilters="listFilters" :parameters="listParameters"
                   @dblclick="tank => $router.push({name:'EquipmentTanksTransactions',params:{tankId:tank.id}})"
                   @itemExpanded="({item, value}) => expand(item, value)"
                   :item-class="tank => (tank.active ? '' : 'grey--text')"
                   title="Tanks"
                   :print-info="{pageSize:'landscape',
                                 subTitle:[`As of ${$formatDate(filters.asof)}`,filters.maintenanceDue=='1'?'Maintenance due':'',filters.status!='1'?(filters.status=='0'?'Inactive':'All'):'',
                                            filters.siteId!=null?siteName:'',filters.contents!='0'?filters.contents=='1'?'Spirits':(filters.contents==='2'?'Wine':'Non-excise'):'',
                                            filters.license!='2'?(filters.license=='0'?'Spirits license':'User license'):'']}"
                   download-endpoint="tanksExportCSV">
      <template v-slot:filter>
        <scan-code @code="scan" return-unmatched-code auto-close/>
        <filter-menu include-reset @reset="filters.siteId=null;filters.status='1';filters.license='2';filters.contents='0';filters.maintenanceDue='0';filters.asof=today;filters.id=null">
          <v-list-item>
            <filter-search :label="[{text:'Name',value:'name'},{text:'Lot',value:'contents.lot'}]" v-model="filters.search" :search-field.sync="filters.searchField" :search-display.sync="searchDisplay"/>
          </v-list-item>
          <v-list-item class="mt-n5">
            <v-checkbox v-model="filters.maintenanceDue" true-value="1" false-value="0" label="Maintenance due"/>
          </v-list-item>
          <v-list-item>
            <v-autocomplete label="Status" dense outlined :items="[{value:'2',text:'All'},{divider:true},{value:'1',text:'Active'},{value:'0',text:'Inactive'}]" v-model="filters.status"/>
          </v-list-item>
          <v-list-item v-if="sites.length>1">
            <v-autocomplete label="Site" dense outlined :items="[{id:null,name:'All'},{divider:true},...sites]" v-model="filters.siteId" item-text="name" item-value="id"/>
          </v-list-item>
          <v-list-item>
            <v-autocomplete label="Contents" dense outlined :items="[{value:'0',text:'Any'},{divider:true},{value:'1',text:'Spirits'},{value:'2',text:'Wine'},{value:'3',text:'Non-excise'}]" v-model="filters.contents"/>
          </v-list-item>
          <v-list-item>
            <v-autocomplete label="License" dense outlined :items="[{value:'2',text:'All'},{divider:true},{value:'1',text:'User'},{value:'0',text:'Spirits'}]" v-model="filters.license"/>
          </v-list-item>
          <v-list-item>
            <date-picker label="As of" v-model="filters.asof" date-only/>
          </v-list-item>
        </filter-menu>
        <template v-if="!$vuetify.breakpoint.mobile">
          <v-chip v-show="filters.id" close small color="primary" class="ml-1" @click:close="filters.id=null">Scanned tank</v-chip>
          <v-chip v-show="filters.search" close small class="ml-1" @click:close="filters.search=''">{{searchDisplay}}</v-chip>
          <v-chip v-show="filters.asof!==today" close small class="ml-1" @click:close="filters.asof=today">As of {{filters.asof | formatDate}}</v-chip>
          <v-chip v-show="filters.maintenanceDue=='1'" close small class="ml-1" @click:close="filters.maintenanceDue='0'">Maintenance due</v-chip>
          <v-chip v-show="filters.status!='1'" close small class="ml-1" @click:close="filters.status='1'">{{filters.status=='0'?'Inactive':'All'}}</v-chip>
          <v-chip v-show="filters.siteId!=null" close small class="ml-1" @click:close="filters.siteId=null">{{siteName}}</v-chip>
          <v-chip v-show="filters.contents!='0'" close small class="ml-1" @click:close="filters.contents='0'">{{filters.contents=='1'?'Spirits':(filters.contents==='2'?'Wine':'Non-excise')}}</v-chip>
          <v-chip v-show="filters.license!='2'" close small class="ml-1" @click:close="filters.license='2'">{{filters.license=='0'?'Spirits license':'User license'}}</v-chip>
        </template>
      </template>
      <template v-slot:menu>
        <template v-if="!readOnly">
          <v-list-item :to="{name:'EquipmentTanksEditNew'}"><v-list-item-title>Create tank</v-list-item-title></v-list-item>
          <v-list-item @click="doImport=true" v-if="!$vuetify.breakpoint.mobile"><v-list-item-title>Import tanks from CSV</v-list-item-title></v-list-item>
        </template>
        <template v-if="$store.getters.company.connectedToAccounting">
          <v-divider/>
          <v-list-item @click="sync"><v-list-item-title>Sync tanks with QBO</v-list-item-title></v-list-item>
        </template>
        <v-divider/>
        <v-list-item :to="{name:'ReportsView',params:{back:[{text:'Tanks',to:{name:'EquipmentTanks'}}]},query:{to:'reports/inventory/ReportsInventoryTanksPhysical'}}"><v-list-item-title>Physical inventory worksheet</v-list-item-title></v-list-item>
        <v-list-item :to="{name:'ExciseVesselReconcile',params:{type:'Tank'}}"><v-list-item-title>Reconcile tank inventory</v-list-item-title></v-list-item>
      </template>

      <template v-slot:footer v-if="selected.length">
        <v-icon style="position: absolute" class="mt-3 ml-3">mdi-arrow-up-left</v-icon>
        <equipment-tanks-batch-edit :tanks="selected" @refresh="$refs.table.buildList($event)"/>
      </template>
      <template v-slot:item.name="{item,value,printing}">
        <template v-if="printing">{{value}}</template>
        <template v-else>
          <v-hover v-if="!readOnly" v-slot="{hover}">
            <a @click.stop="$router.push({name:'EquipmentTanksEdit',params:{tankId:item.id}})"
                :class="{'text-decoration-underline':hover,'red--text':item.maintenanceDue,'grey--text':!item.active}">{{item.name}}</a>
          </v-hover>
          <div v-else :class="{'red--text':item.maintenanceDue,'grey--text':!item.active}">{{item.name}}</div>
          <div v-if="item.maintenanceDue" class="grey--text text-caption">
            <v-btn plain color="error" x-small :to="{name:'EquipmentMaintenance',params:{equipmentId:item.id}}" class="px-0">
              Maintenance due
            </v-btn>
          </div>
          <div v-else-if="!item.active" class="grey--text text-caption">Inactive</div>
          <div v-else-if="item.contents.userLicense" class="grey--text text-caption">User License</div>
        </template>
      </template>
      <template v-slot:item.contents.alcoholName="{item,printing}">
        <template v-if="item.contents.volume!==0">
          <template v-if="printing">{{item.contents.alcoholName}}</template>
          <div v-else :class="{'green--text':item.contents.proofed}">
            {{item.contents.alcoholName}}
            <div v-if="item.contents.proofed" class="text-caption">Proofed to {{item.contents.proofed | number(2)}}% alc./vol.</div>
            <template v-if="item.contents.alcoholType!=='Spirit'">
              <div v-if="item.contents.alcoholType==='NonExcise'" class="text-caption grey--text">Non-excise</div>
              <div v-else class="text-caption">Wine</div>
            </template>
            <div v-else-if="item.contents.imported" class="text-caption grey--text">Imported</div>
          </div>
        </template>
        <template v-else>&nbsp;</template>
      </template>
      <template v-slot:item.contents.lot="{item,printing}">
        <template v-if="printing">{{item.contents.lot}}</template>
        <v-hover v-else-if="item.contents.lot" v-slot="{hover}">
          <a @click.stop="$router.push({name:'ReportsView',params:{back:[{text:'Tanks',to:{name:'EquipmentTanks'}}]},query:{to:'reports/inventory/ReportsLot',id:item.id,lot:item.contents.lot,forward:1}})"
            :class="{'text-decoration-underline':hover}">{{item.contents.lot}}</a>
        </v-hover>
      </template>
      <template v-slot:item.contents.volume="{item}">
        <template v-if="item.contents.alcoholType === 'Spirit' || !item.contents.volume">
          {{item.contents.volume | number(3)}} LAA
        </template>
        <template v-else>
          {{item.contents.volume | number(3)}} litres
        </template>
      </template>
      <template v-slot:item.contents.litres="{value}">
        {{value | number(3)}}
      </template>
      <template v-slot:item.contents.strength="{value}">
        {{value | number(2)}}%
      </template>
      <template v-slot:item.contents.kg="{value}">
        {{value | number(3)}} kg
      </template>
      <template v-slot:item.contents.value="{value}">
        {{value | currency}}
      </template>
      <template v-slot:item.maintenanceDue="{value}">
        {{value | formatDate}}
      </template>
      <template v-slot:item.contents.userLicense="{value,printing}">
        <template v-if="printing">{{value?'Y':''}}</template>
        <v-icon v-else-if="value">mdi-check</v-icon>
      </template>
      <template v-slot:item.contents.imported="{value,printing}">
        <template v-if="printing">{{value?'Y':''}}</template>
        <v-icon v-else-if="value">mdi-check</v-icon>
      </template>
      <template v-slot:item.contents.proofed="{value,printing}">
        <template v-if="printing">{{value?'Y':''}}</template>
        <v-icon v-else-if="value">mdi-check</v-icon>
      </template>
      <template v-slot:item.capacity="{value}">
        {{value | number(0)}}
      </template>
      <template v-slot:header.action v-if="!readOnly">
        <v-btn fab color="primary" small :to="{name:'EquipmentTanksEditNew'}"><v-icon>mdi-plus</v-icon></v-btn>
      </template>
      <template v-slot:item.action="{item}">
        <equipment-tank-menu :tank="item" :readOnly="readOnly" @delete="tankToDelete=item;showDelete=true"/>
      </template>
      <template v-slot:body.append="{headers,printing}" v-if="!$vuetify.breakpoint.mobile">
        <tr :class="darkMode ? 'total-row-dark' : 'total-row'">
          <template v-for="header of headers">
            <td class="text-right" v-if="header.value==='contents.volume'">
              {{totals.totalVolume | number(3)}}
            </td>
            <td class="text-right" v-else-if="header.value==='contents.litres'">
              {{totals.totalLitres | number(3)}}
            </td>
            <td class="text-right" v-else-if="header.value==='contents.value' && values">
              {{totals.totalValue | currency}}
            </td>
            <td v-else-if="!printing || header.print === undefined || header.print"/>
          </template>
        </tr>
      </template>
      <template v-slot:expanded-item="{ headers,item }">
        <equipment-list-expand :width="headers.length" :equipment="item"/>
      </template>
    </purtrak-table>

    <delete-object v-model="showDelete" :name="tankToDelete.name" @delete="performDeleteTank"/>
  </div>
</template>

<script>
import EquipmentTankMenu from '@/views/equipment/tanks/EquipmentTankMenu'
import EquipmentListExpand from '@/views/equipment/EquipmentListExpand'
import EquipmentTanksBatchEdit from '@/views/equipment/tanks/EquipmentTanksBatchEdit'
import {format} from 'date-fns'

export default {
  name: 'EquipmentTanks',
  components: {EquipmentTankMenu,EquipmentListExpand,EquipmentTanksBatchEdit},
  data() {
    return {
      maintenanceDueCount: 0,
      showStatus: false,
      filters: {
        maintenanceDue: '0',
        asof: format(new Date(), 'yyyy-MM-dd'),
        status: '1',
        siteId: null,
        search: '',
        searchField: 'name',
        contents: '0',
        license: '2',
        id: null
      },
      totals: {totalValue:0, totalLitres:0, totalVolume:0},
      today: format(new Date(), 'yyyy-MM-dd'),
      searchDisplay: '',
      loading: true,
      tankToDelete: {},
      showDelete: false,
      doImport: false,
      importFields: [{name:'Name',mapTo:'',mandatory:true},{name:'Capacity',mapTo:'',mandatory:false},{name:'Tare',mapTo:'',mandatory:false},
                     {name:'Alcohol type',mapTo:'',mandatory:false},{name:'Alcohol name',mapTo:'',mandatory:false},
                     {name:'Initial volume',mapTo:'',mandatory:false},{name:'Initial litres',mapTo:'',mandatory:false},
                     {name:'Initial value',mapTo:'',mandatory:false},
                     {name:'Initial date',mapTo:'',mandatory:false,date:true},{name:'Initial lot',mapTo:'',mandatory:false}],
      instructions: `<ol class="mb-4">
                      <li>Name</li>
                      <li>Capacity (optional, default is 0 litres)</li>
                      <li>Tare (optional, default is 0 kg)</li>
                      <li>Alcohol type ("Spirit", "Wine" or "Non-Excise") to specify initial contents (optional)</li>
                      <li>Alcohol name, i.e. the name of the spirit,wine, or non-excise, if alcohol type was specified</li>
                      <li>Initial volume (optional, default is 0 LAA)</li>
                      <li>Initial litres (optional, default is 0)</li>
                      <li>Initial value (optional, default is $0)</li>
                      <li>Initial date (optional, default is today)</li>
                      <li>Initial lot (optional, default is L0)</li>
                      </ol>
                      <em>Note that not all information for a tank can be imported.</em>
                      You should, therefore, check each imported tank to ensure all settings are correct.`,
      defaultColumns: [{ value: 'name', visible:true},
                       { value: 'site.name', visible:true},
                       { value: 'contents.alcoholName', visible:true},
                       { value: 'contents.lot', visible:true},
                       { value: 'contents.volume', visible:true},
                       { value: 'contents.litres', visible:false},
                       { value: 'contents.strength', visible:false},
                       { value: 'contents.kg', visible:false},
                       { value: 'contents.value', visible:true},
                       { value: 'capacity', visible:false},
                       { value: 'maintenanceDue', visible:false},
                       { value: 'contents.userLicense', visible:false},
                       { value: 'contents.imported', visible:false},
                       { value: 'contents.proofed', visible:false}],
      headers: [{ text: 'TANK', value: 'name', width:'20%',sortable:true},
                { text: 'SITE', value: 'site.name', width:'19%',sortable:true,omit:() => this.sites.length <= 1},
                { text: 'CONTENTS', value: 'contents.alcoholName', width:'20%',sortable:true},
                { text: 'LOT', value: 'contents.lot', width:'10%',sortable:true},
                { text: 'VOLUME', value: 'contents.volume', width:'12%',sortable:true,align:'right'},
                { text: 'LITRES', value: 'contents.litres', width:'12%',sortable:true,align:'right', omit:() => !this.trackLitres},
                { text: 'STRENGTH', value: 'contents.strength', width:'12%',sortable:true,align:'right', omit:() => !this.trackStrength},
                { text: 'WEIGHT', value: 'contents.kg', width:'12%',sortable:true,align:'right', omit:() => !this.trackKg},
                { text: 'VALUE', value: 'contents.value', width:'12%',sortable:true,align:'right', omit:() => !this.values},
                { text: 'CAPACITY', value: 'capacity', width:'12%',sortable:true,align:'right'},
                { text: 'MAINTENANCE', value: 'maintenanceDue', width:'15%',sortable:true},
                { text: 'USER LIC', value: 'contents.userLicense', width:'10%',sortable:true,align:'center'},
                { text: 'IMPORTED', value: 'contents.imported', width:'10%',sortable:true,align:'center'},
                { text: 'PROOFED', value: 'contents.proofed', width:'10%',sortable:true,align:'center'}
                ]
    }
  },
  computed: {
    listFilters() {
      const filters = [];
      if (this.filters.id)
        filters.push({field:'id', comparator:'eq', value:this.filters.id});
      if (this.filters.maintenanceDue == '1')
        filters.push({field:'maintenanceDue', comparator:'ne', value:'null'});
      if (this.filters.status == '1' || this.filters.status == '0')
        filters.push({field:'active', comparator:'eq', value:this.filters.status == '1' ? 'true' : 'false'});

      if (this.filters.contents == '1')
        filters.push({field:'alcoholType', comparator:'eq', value:'Spirit'});
      else if (this.filters.contents == '2')
        filters.push({field:'alcoholType', comparator:'ne', value:'Spirit'},{field:'alcoholType', comparator:'ne', value:'NonExcise'});
      else if (this.filters.contents == '3')
        filters.push({field:'alcoholType', comparator:'eq', value:'NonExcise'});

      if (this.filters.license == '1')
        filters.push({field:'userLicense', comparator:'eq', value:'true'});
      else if (this.filters.license == '0')
        filters.push({field:'userLicense', comparator:'eq', value:'false'});

      if (this.filters.siteId)
        filters.push({field:'siteId', comparator:'eq', value:this.filters.siteId});

      if (this.filters.search)
        filters.push({field:this.filters.searchField, comparator:'contains', value:this.filters.search});

      return filters;
    },
    listParameters() {
      return this.filters.asof ? [{name:'asof', value:`${this.filters.asof} 23:59:59`}] : [];
    },
    trackLitres() {
      return this.$store.getters.company.settings.trackLitres;
    },
    trackKg() {
      return this.$store.getters.company.settings.trackKg;
    },
    trackStrength() {
      return this.$store.getters.company.settings.trackStrength;
    },
    sites() {
      return this.$store.getters.sites(true, {B263:true,B265:true,B266:true});
    },
    siteName() {
      return this.sites.find(site => site.id == this.filters.siteId)?.name;
    },
    readOnly() {
      return !this.$store.getters.company.permissions.includes('production.write');
    },
    values() {
      return this.$store.getters.company.permissions.includes('values');
    }
  },
  async created() {
    this.$store.dispatch('checkTasks');

    // Maintenance due?
    const results = await this.$apiCall(`query EquipmentTanks {
                                            equipmentMaintenanceDue{typeName}
                                          }`,
                                      {});
    if (!results.error)
      this.maintenanceDueCount = results.data.equipmentMaintenanceDue.filter(e => e.typeName === 'Tank').length;

    if (!['EquipmentTanksEdit', 'EquipmentTanksEditNew', 'ExciseVesselTransactions'].includes(this._previousRoute) &&
        Object.keys(this.$route.query).length === 0 &&
        this.maintenanceDueCount && !localStorage.getItem('hideNotification')) {
      this.$nextTick(() => this.showStatus = true);
      setTimeout(() => this.showStatus = false, 10_000);  // Close in 10 seconds
    }
  },
  methods: {
    scan({type, id}) {
      if (type === 'Tank')
        Object.assign(this.filters, {siteId:null,status:'2',license:'2',contents:'0',maintenanceDue:'0',asof:this.today,id});
    },
    listBuilt({list}) {
      if (this.filters.id && list.edges.length === 1)
        this.$refs.table.expanded.push(list.edges[0].node);
    },
    async expand(item, value) {
      if (value && !item.maintenance) {
        this.loading = true;
        const results = await this.$apiCall(`query EquipmentTanks($id:ID!) {
                                                  tank(id:$id) {
                                                    maintenance {frequency,frequencyUnits,nextDate,description}
                                                  }
                                                }`,
                                            {id:item.id});
        this.$set(item, 'maintenance', results.data.tank.maintenance);
        this.loading = false;
      }
    },
    async refresh() {
      this.$refs.table.buildList();
    },
    async performDeleteTank() {
      this.loading = true;
      const results = await this.$apiCall(`mutation EquipmentTanks($id: ID!) {
                                             objectDelete(id:$id)
                                           }`,
                                          {id:this.tankToDelete.id});
      this.loading = false;
      if (!results.error) {
        this.showDelete = false;
        this.$store.commit('setError', {type:'success',message:`Deleted tank ${this.tankToDelete.name}`});
        this.refresh();
      }
    },
    async sync() {
      this.loading = true;
      const results = await this.$apiCall(`mutation EquipmentTanks {
                                             accountingIntegrationSync(step:"tanks") {name}
                                           }`, {});
      await this.refresh();
      if (!results.error)
        this.$store.commit('setError', {type:'success',message:`Synced tanks with QBO`});
    }
  }
}
</script>